import PropTypes from 'prop-types'
import React from 'react'
import {Content, isEditing} from '@builder.io/sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {useGetTpaBanner} from '../../hooks/useGetTpaBanner'
import {useFetchOneEntry} from 'hec/hooks/useFetchOneEntry'

import {customComponents} from 'hec/builder'
import modelMap from 'hec/builder/model-map'
import {useAppOrigin} from '~/app/hooks/use-app-origin'
import {cn} from 'hec/lib/utils'

import SiteToggle from '../../../components/site-toggle'

export const AlertBar = ({displayTpaBannerInEditor, tpaBannerToDisplayInEditor, attributes}) => {
    const {className, ...rest} = attributes || {}
    const config = getConfig()
    const appOrigin = useAppOrigin()
    const isEdit = isEditing()
    const threeLetter = config.app.threeLetter

    const {data: tpaBannerData, isLoading: tpaBannerDataLoading} = useGetTpaBanner({
        isEdit,
        displayTpaBannerInEditor,
        tpaBannerToDisplayInEditor
    })
    const shouldQuery = true
    const {
        data: alertBarContent,
        error: alertBarError,
        isLoading: alertBarLoading
    } = useFetchOneEntry({
        queryKey: ['alert-bar'],
        options: {
            // NOTE: Builder scheduling (startDate/endDate) only works when userAttributes
            // are present. Passing an empty object disables that evaluation, so we include
            // a minimal attribute (e.g. `date: Date.now()`) to ensure scheduled content resolves correctly.
            userAttributes: {
                date: Date.now()
            },
            model: modelMap.alertBar,
            apiKey: config.app.builder.api
        },
        enabled: shouldQuery
    })

    const tpaBannerContent = tpaBannerData?.data?.bannerContent

    if (alertBarError) {
        console.error('An error occurred while fetching alert bar content: ', alertBarError)
    }

    if (tpaBannerDataLoading || alertBarLoading) {
        return null
    }

    if (tpaBannerContent) {
        const isDevelopment = process.env.NODE_ENV === 'development'
        const baseUrl = isDevelopment ? `https://${threeLetter}.devhec.com` : appOrigin

        const baseImageUrl = `${baseUrl}/on/demandware.static/-/Sites/default/`

        const modifiedHtml = tpaBannerContent
            .replaceAll(/<li([^>]*)>/g, (match, group) => {
                const cleanGroup = group.replace(/\s*style="[^"]*"/g, '')
                return `<li${cleanGroup} class="mrt_tpa_banner header-banner" style="display: flex;text-transform: uppercase;justify-content:center;align-items: center;flex-direction:row;">`
            })
            .replaceAll(
                /src="(?!https?:\/\/|\/\/)([^"]+)"/g,
                (match, path) => `src="${baseImageUrl}${path}"`
            )

        return (
            <div
                className={cn(
                    'relative bg-gray-01 text-uppercase text-black',
                    'h-[85px] lg:h-[35px]',
                    className
                )}
                {...rest}
            >
                <SiteToggle />
                <div dangerouslySetInnerHTML={{__html: modifiedHtml}} className="pt-2" />
            </div>
        )
    }

    return (
        alertBarContent && (
            <div className={cn('relative h-[85px] lg:h-[35px]', className)} {...rest}>
                <SiteToggle />
                <div className="h-full [&>*]:h-full [&>*>*]:h-full">
                    <Content
                        model={modelMap.alertBar}
                        content={alertBarContent}
                        apiKey={config.app.builder.api}
                        customComponents={customComponents}
                        data-testid="alertBar-Content"
                    />
                </div>
            </div>
        )
    )
}

AlertBar.propTypes = {
    children: PropTypes.arrayOf(PropTypes.element),
    displayTpaBannerInEditor: PropTypes.bool,
    tpaBannerToDisplayInEditor: PropTypes.object,
    attributes: PropTypes.object
}

export const AlertBarDefinition = {
    component: AlertBar,
    name: 'AlertBar',
    override: true,
    canHaveChildren: true,
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    inputs: [
        {
            name: 'displayTpaBannerInEditor',
            type: 'boolean',
            defaultValue: false
        },
        {
            name: 'tpaBannerToDisplayInEditor',
            type: 'reference',
            model: modelMap.tpaWelcomeBanner,
            required: true,
            showIf: `options.get('displayTpaBannerInEditor') === true`
        }
    ]
}
